import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BudgetsContent from 'src/components/BudgetsContent/BudgetsContent'
import BudgetsAllBudgetsTab from 'src/page-components/Budgets/BudgetsAllBudgetsTab/BudgetsAllBudgetsTab'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectBudgetsAllBudgetsMatch } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { isUpdatedAllBudgetsTableEnabled } from 'src/utils/featureFlags'

/**
 * The legacy org-wide budgets list, and — while EX-40737 rolls out — the gate in
 * front of its replacement.
 *
 * The gate lives HERE rather than at either route because this component is the
 * one thing both entry points share: the flat `/budgets` family route renders it
 * through `BudgetsFamilyPage`, and My Wallet → Budgets renders it directly
 * through `myWalletBudgets.appRoute`. Gating at the routes would mean two copies
 * of the same condition, and a surface that quietly kept the old table if only
 * one of them was updated.
 *
 * On, this forwards to the redesigned page and renders none of what is below —
 * `BudgetsAllBudgetsTab` brings its own `<Helmet>` and page content wrapper,
 * which is why this is an early return rather than a swap of the inner content.
 * Off, everything below is byte-for-byte what shipped before.
 */
const BudgetsAllBudgets: React.FC = () => {
  const appName = useSelector(selectAppName)
  const budgetsAllBudgetsMatch = useSelector(selectBudgetsAllBudgetsMatch)
  const showUpdatedAllBudgetsTable = isUpdatedAllBudgetsTableEnabled(
    useSelector(selectFeatureFlaggerFromState),
  )
  const allOrYour = budgetsAllBudgetsMatch ? 'All' : 'Your'

  if (showUpdatedAllBudgetsTable) {
    return <BudgetsAllBudgetsTab />
  }

  return (
    <>
      <Helmet>
        <title>{`${allOrYour} Budgets | ${appName}`}</title>
        <meta
          name="description"
          content={`${allOrYour} Budgets page of ${appName}`}
        />
      </Helmet>
      <BackgroundPageContent noStackingIndex noPadding>
        <BudgetsContent />
      </BackgroundPageContent>
    </>
  )
}

export default BudgetsAllBudgets
